import { Box, Chip } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { DataGridComponent } from "../common/DataGridComponent";
import { GridColDef, GridSortModel, getGridSingleSelectOperators } from "@mui/x-data-grid-pro";
import { useAppSelector, AppState, useAppDispatch } from "../../store";
import { setPermissionRowData } from "../../store/slices/usersSlice";
import { usePermission } from "../../hooks/usePermission";
import { initialPaginationModelValues } from "../../constants/paginationConstants";
import { debounce } from "lodash";
import { useSnackbar } from "../../hooks/useSnackbar";

const PermissionsList: React.FC = () => {
  const rowData = useAppSelector(
    (state: AppState) => state.usersSlice.permissionRowData
  );
  const { handleError } = useSnackbar();
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModelValues
  );
  const [filterObj, setFilterObject] = useState("");

  const dispatch = useAppDispatch();
  const {
    getAllPermissions,
    allPermissionData,
    allPermissionIsLoading,
    allPermissionError,
    allPermissionIsError,
  } = usePermission();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "code", sort: "asc" },
  ]);
  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(model);
    }, 300),
    []
  );
  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: "Code",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 160,
      flex: 2,
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 100,
      type:'singleSelect',
      valueOptions:[{value:true,label:'Yes'},{value:false,label:'No'}],
      filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === "is"),
      renderCell: (params: any) =>
        params.row?.is_active ? (
          <Chip label="Yes" color="success" size="small" variant="outlined" />
        ) : (
          <Chip label="No" color="error" size="small" variant="outlined" />
        ),
    },
  ];

  const getAllPermissionsFunc = (
    from: number,
    size: number,
    filterObj?: any
  ) => {
    getAllPermissions({
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data:filterObj && filterObj?.advanceFilter
    });
  };

  useEffect(() => {
    if (paginationModel?.pageSize) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getAllPermissionsFunc(0, pageSize, filterObj);
      } else {
        getAllPermissionsFunc(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, filterObj]);

  useEffect(() => {
    if (allPermissionData && allPermissionData?.count > 0) {
      setRowsCount(allPermissionData?.count);

      const data = allPermissionData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele.code,
        };
      });
      dispatch(setPermissionRowData(data));
    } else if (allPermissionData?.count == 0) {
      setRowsCount(0);
      dispatch(setPermissionRowData([]));
    }
  }, [allPermissionData]);

  useEffect(() => {
    if (allPermissionIsError) {
      dispatch(setPermissionRowData([]));
      setRowsCount(0);
      const error: any = allPermissionError;
      handleError(error);
    }
  }, [allPermissionIsError]);

  return (
    <>
      <Box sx={{ width: "100%", height: "calc(100vh - 234px)" }}>
        <DataGridComponent
          columns={columns}
          rows={rowData ?? []}
          showQuickFilter
          paginationMode="server"
          rowsCount={rowsCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={allPermissionIsLoading}
          filterMode="server"
          filterValues={(data: any) => setFilterObject(data)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          exportFileName="Permission_List"
        />
      </Box>
    </>
  );
};

export default PermissionsList;
